import { strToU8, compressSync } from 'fflate';
import { md5 } from 'super-fast-md5';
import { getToken } from './token';
import { getApiBaseUrl as getApiBaseUrlFromEnv, resolveEnvironment } from '@seede/api-client';
import { createVeesoAPI } from '@seede/api-client/veeso';
import { deserialize } from '@seede/renderer/lib/export';

const getApiBaseUrl = (): string => getApiBaseUrlFromEnv(resolveEnvironment());

const veesoClient = createVeesoAPI({
  baseURL: getApiBaseUrl(),
  staticBaseURL: 'https://static.veeso.ai',
  getToken: () => getToken(),
});

const { api, getAssetURL } = veesoClient;

export { veesoClient, api, getAssetURL };

// Inline simple utility functions to avoid importing @seede/renderer in landing page
// These are lightweight copies of the original functions
function createId(): string {
  return Math.random().toString(36).substr(2, 9);
}

function copy<T>(data: T): T {
  return JSON.parse(JSON.stringify(data));
}

/**
 * 获取用户 token
 * 直接使用独立的 token 模块，避免 composable 调用时机问题
 */
export function getUserToken(): string | null {
  return getToken();
}

export function getAPI(): string {
  return getApiBaseUrl();
}

export class APIError extends Error {
  status: number;
  data?: unknown;
  code?: string;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.message = message;
  }
}

interface RequestOptions extends RequestInit {
  query?: Record<string, string>;
}

interface RequestConfig {
  type?: 'json' | 'blob' | 'text';
  maxRetries?: number;
  retryDelay?: number;
}

export async function request(
  url: string,
  options: RequestOptions = {},
  { type = 'json', maxRetries = 1, retryDelay = 1000 }: RequestConfig = {}
): Promise<unknown> {
  const method = options.method || 'GET';

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const headers = options.headers as Record<string, string> | undefined;
      const token = getUserToken();
      const api = getAPI();
      let requestUrl = '';

      if (url.startsWith('http')) {
        requestUrl = url;
      } else {
        requestUrl = `${api}${url}`;
      }

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        requestUrl += `?${queryString}`;
      }

      // Build headers object, only add authorization if token exists
      const requestHeaders: Record<string, string> = {
        'content-type': 'application/json',
        ...headers,
      };

      if (token) {
        requestHeaders.authorization = `Bearer ${token}`;
      }

      const res = await fetch(requestUrl, {
        ...options,
        headers: requestHeaders,
      });

      if (!res.ok) {
        const errorData = await res.json();
        if (errorData.message) {
          const err = new APIError(
            res.status,
            errorData.message || `HTTP error! status: ${res.status} ${method} ${url}`
          );
          err.data = errorData;
          err.code = errorData.code;

          // Don't retry for client errors (4xx) except 429 (rate limit)
          if (res.status >= 400 && res.status < 500 && res.status !== 429) {
            throw err;
          }

          // Retry for server errors (5xx) and rate limits (429)
          if (attempt < maxRetries && (res.status >= 500 || res.status === 429)) {
            const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
            console.warn(
              `Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`
            );
            await new Promise((resolve) => setTimeout(resolve, delay));
            continue;
          }

          throw err;
        }

        const err = new APIError(
          res.status,
          errorData.error || `HTTP error! status: ${res.status} ${method} ${url}`
        );

        // Don't retry for client errors (4xx) except 429 (rate limit)
        if (res.status >= 400 && res.status < 500 && res.status !== 429) {
          throw err;
        }

        // Retry for server errors (5xx) and rate limits (429)
        if (attempt < maxRetries && (res.status >= 500 || res.status === 429)) {
          const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
          console.warn(
            `Request failed (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }

        throw err;
      }

      if (type === 'json') {
        const data = await res.json();
        return data;
      } else if (type === 'blob') {
        const data = await res.blob();
        return data;
      } else {
        const data = await res.text();
        return data;
      }
    } catch (error) {
      if (error instanceof APIError) {
        throw error;
      }

      const err = error as Error;
      // Retry for network errors
      if (attempt < maxRetries && (err.name === 'TypeError' || err.message.includes('fetch'))) {
        const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
        console.warn(
          `Network error (attempt ${attempt + 1}/${maxRetries + 1}), retrying in ${delay}ms...`,
          err.message
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      console.error('Request failed:', error);
      throw new Error(`Request failed: ${err.message} ${method} ${url}`);
    }
  }
}

interface StreamCallbacks {
  onMessage?: (data: unknown) => void;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export async function requestStream(
  url: string,
  options: RequestOptions = {},
  { onMessage, onError, onComplete }: StreamCallbacks = {}
): Promise<{ abort: () => void }> {
  try {
    const headers = options.headers as Record<string, string> | undefined;
    const token = await getUserToken();
    const api = getAPI();

    // Build headers object, only add authorization if token exists
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...headers,
    };

    if (token) {
      requestHeaders.authorization = `Bearer ${token}`;
    }

    const res = await fetch(`${api}${url}`, {
      ...options,
      headers: requestHeaders,
    });

    if (!res.ok) {
      const errorData = await res.json();
      const errorMessage =
        errorData.message || errorData.error || `HTTP error! status: ${res.status}`;
      if (onError) {
        onError(new Error(errorMessage));
      }
      throw new Error(errorMessage);
    }

    const reader = res.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const processStream = async () => {
      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            if (onComplete) {
              onComplete();
            }
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          // Process complete SSE messages
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // Keep the last incomplete chunk in buffer
          for (const line of lines) {
            if (line.trim() && line.startsWith('data:')) {
              const data = line.substring(5).trim();
              if (data !== '[DONE]' && onMessage) {
                onMessage(JSON.parse(data));
              }
            }
          }
        }
      } catch (error) {
        if (onError) {
          onError(error as Error);
        }
        throw error;
      }
    };

    // Start processing the stream
    processStream();

    // Return an object that allows aborting the stream
    return {
      abort: () => {
        reader.cancel();
        if (onComplete) {
          onComplete();
        }
      },
    };
  } catch (error) {
    console.error('Stream request failed:', error);
    if (onError) {
      onError(error as Error);
    }
    throw new Error(`Stream request failed: ${(error as Error).message}`);
  }
}

interface ProjectData {
  data?: unknown;
  [key: string]: unknown;
}

export async function addProject(project: ProjectData): Promise<unknown> {
  const result = await request('/project', {
    method: 'POST',
    body: JSON.stringify({
      ...project,
      data: btoa(unescape(encodeURIComponent(JSON.stringify(project.data)))),
    }),
  });
  return result;
}

interface PutProjectPayload {
  data?: unknown;
  thumbnail?: string;
  thumbnailData?: string;
  [key: string]: unknown;
}

export async function putProject(id: string, payload: PutProjectPayload): Promise<unknown> {
  const { data, thumbnail } = payload;
  if (data) {
    delete payload.data;
  }
  if (thumbnail) {
    payload.thumbnailData = thumbnail;
  }

  const result = await request(`/project/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result;
}

interface Context {
  api: {
    getFrames: () => Array<{ w: number; h: number; name?: string }>;
    exportImage: (options: {
      frame?: unknown;
      contentType: string;
      dpr: number;
      quality: number;
      fast?: boolean;
    }) => Promise<string>;
    exportState: (options: { dropMessages: boolean }) => Promise<unknown>;
  };
  providers?: {
    state: {
      disposed: boolean;
      state: unknown;
    };
  };
  card?: unknown;
  debug?: boolean;
  disposed?: boolean;
}

export async function updateProjectThumbnail(id: string, context: Context): Promise<unknown> {
  let thumbnail = '';
  const frame = context.api.getFrames()[0];
  if (!frame) return;

  // Smart DPR optimization based on frame dimensions
  let dpr = 1;
  const frameArea = frame.w * frame.h;
  const maxDimension = Math.max(frame.w, frame.h);

  // Calculate optimal DPR based on frame size and area
  if (frameArea > 4000000) {
    // Very large frames (>4MP)
    dpr = 0.3;
  } else if (frameArea > 2000000) {
    // Large frames (>2MP)
    dpr = 0.4;
  } else if (maxDimension > 2000) {
    // High resolution in one dimension
    dpr = 0.5;
  } else if (maxDimension > 1440) {
    // Medium-large frames
    dpr = 0.6;
  } else if (maxDimension > 800) {
    // Medium frames
    dpr = 0.8;
  } else {
    dpr = 1; // Small frames can use full resolution
  }

  // Ensure minimum quality for very small frames
  if (maxDimension < 400) {
    dpr = Math.min(dpr, 2); // Allow upscaling for small frames
  }

  thumbnail = await context.api.exportImage({
    contentType: 'image/webp',
    dpr: dpr,
    quality: 0.4,
    fast: true,
  });

  if (!thumbnail) {
    return;
  }

  // Convert data URL to Blob for multipart/form-data upload
  const response = await fetch(thumbnail);
  const blob = await response.blob();

  // Create FormData for file upload
  const formData = new FormData();
  formData.append('thumbnail', blob, 'thumbnail.webp');

  const token = await getUserToken();
  const api = getAPI();
  const requestUrl = `${api}/project/${id}/thumbnail`;

  // Build headers object, only add authorization if token exists
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(requestUrl, {
    method: 'PUT',
    headers,
    body: formData,
  });

  if (!res.ok) {
    const errorData = await res.json();
    throw new APIError(res.status, errorData.message || 'Failed to update thumbnail');
  }

  const result = await res.json();
  return result;
}

export async function updateProjectByData(id: string, payload: unknown): Promise<unknown> {
  const result = await request(`/project/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  return result;
}

interface SnapshotData {
  thumbnail: string;
  data: {
    type: string;
    content: string;
  };
}

export async function createSnapshotData(context: Context, frame: unknown): Promise<SnapshotData> {
  const [card, thumbnail] = await Promise.all([
    context.api.exportState({ dropMessages: true }),
    context.api.exportImage({
      frame: frame,
      contentType: 'image/webp',
      dpr: 1,
      quality: 0.4,
    }),
  ]);

  const originalContent = JSON.stringify(card);
  const buf = strToU8(originalContent);
  const compressed = compressSync(buf, { level: 6, mem: 8 });
  const compressedContent = btoa(String.fromCharCode(...compressed));

  const snapshot = {
    thumbnail: thumbnail,
    data: {
      type: 'gz',
      content: compressedContent,
    },
  };

  return snapshot;
}

// Placeholder for getTemplateCopy - should be imported or defined elsewhere
declare function getTemplateCopy(): Promise<(data: unknown) => unknown>;

interface ProjectPayload {
  hash: string;
  timestamp: number;
  attemptId: string;
  meta?: { name: string };
  data?: unknown;
}

async function prepareProjectPayload(context: Context): Promise<ProjectPayload | null> {
  const state = context.providers?.state;
  if (state?.disposed) return null;
  const templateCopy = await getTemplateCopy();
  const cardData = context.card && typeof context.card === 'object' ? context.card : {};
  const stateData = state?.state && typeof state.state === 'object' ? state.state : {};
  const data = templateCopy({
    ...cardData,
    props: {
      ...stateData,
    },
  });

  const currentHash = md5(JSON.stringify(data));

  const payload: ProjectPayload = {
    hash: currentHash,
    timestamp: Date.now(),
    attemptId: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  };

  // Get frame name with fallback handling
  try {
    const frames = context.api.getFrames();
    const firstFrame = frames && frames.length > 0 ? frames[0] : null;
    const name = firstFrame?.name;
    if (name) {
      payload.meta = {
        name,
      };
    }
  } catch (frameError) {
    console.warn('Failed to get frame name:', frameError);
  }

  if (data) {
    try {
      const originalContent = JSON.stringify(data);
      const buf = strToU8(originalContent);
      const compressed = compressSync(buf, { level: 6, mem: 8 });
      const compressedContent = await bufferToBase64(compressed);

      const originalSize = originalContent.length;
      const compressedSize = compressedContent.length;
      const compressionRatio = (((originalSize - compressedSize) / originalSize) * 100).toFixed(2);

      if (context.debug) {
        console.warn(
          `Compression ratio: ${compressionRatio}% (${originalSize} -> ${compressedSize} bytes)`
        );
      }

      payload.data = {
        type: 'gz',
        content: compressedContent,
      };
    } catch (compressionError) {
      console.warn('Compression failed, falling back to uncompressed data:', compressionError);
      const originalContent = JSON.stringify(data);
      const base64Content = btoa(unescape(encodeURIComponent(originalContent)));
      payload.data = base64Content;
    }
  }

  return payload;
}

async function bufferToBase64(buffer: Uint8Array): Promise<string> {
  // use a FileReader to generate a base64 data URI:
  const base64url = await new Promise<string>((r) => {
    const reader = new FileReader();
    reader.onload = () => r(reader.result as string);
    reader.readAsDataURL(new Blob([buffer as any]));
  });
  // remove the `data:...;base64,` part from the start
  return base64url.slice(base64url.indexOf(',') + 1);
}

async function executeProjectUpdate(
  id: string,
  payload: ProjectPayload,
  attempt: number,
  timeout: number
): Promise<unknown> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const result = await request(`/project/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
      signal: controller.signal,
      headers: {
        'X-Attempt': attempt.toString(),
        'X-Attempt-Id': payload.attemptId,
      },
    });

    clearTimeout(timeoutId);

    if (result) {
      return result;
    }

    throw new Error(`Update failed: Unknown error`);
  } catch (requestError) {
    clearTimeout(timeoutId);
    throw requestError;
  }
}

function isRetryableError(error: unknown): boolean {
  const err = error as Error & { status?: number };
  return (
    err.name === 'AbortError' ||
    err.name === 'TimeoutError' ||
    err.message?.includes('network') ||
    err.message?.includes('timeout') ||
    err.message?.includes('fetch') ||
    (err.status !== undefined && err.status >= 500)
  );
}

function calculateRetryDelay(attempt: number): number {
  const baseDelay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
  const jitter = Math.random() * 1000;
  return baseDelay + jitter;
}

// Store last submitted hash for each project
const projectHashCache = new Map<string, string>();

interface UpdateProjectOptions {
  retries?: number;
  timeout?: number;
}

export async function updateProject(
  id: string,
  context: Context,
  options: UpdateProjectOptions = {}
): Promise<unknown> {
  const { retries = 3, timeout = 60000 } = options;
  let lastError: Error | null = null;

  const payload = await prepareProjectPayload(context);
  if (context.disposed) {
    console.warn('Project update skipped: context disposed');
    return;
  }

  if (!payload) {
    console.warn('Project update skipped: no payload');
    return;
  }

  // Check if update is needed by comparing with last submitted hash
  // const lastHash = projectHashCache.get(id);
  // if (lastHash === payload.hash) {
  //   if (context.debug) {
  //     console.info('Project update skipped: no changes detected (MD5 match)');
  //   }
  //   return { skipped: true, reason: 'no_changes' };
  // }

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const result = await executeProjectUpdate(id, payload, attempt, timeout);
      if (context.debug && attempt > 1) {
        console.warn(`Project update succeeded on attempt ${attempt}`);
      }

      // Store the hash after successful update
      projectHashCache.set(id, payload.hash);

      return result;
    } catch (error) {
      lastError = error as Error;

      if (context.debug) {
        console.warn(`Project update attempt ${attempt} failed:`, error);
      }

      if (attempt === retries || !isRetryableError(error)) {
        break;
      }

      const delay = calculateRetryDelay(attempt);

      if (context.debug) {
        console.warn(`Retrying in ${Math.round(delay)}ms... (attempt ${attempt + 1}/${retries})`);
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  const errorMessage = `Failed to update project after ${retries} attempts. Last error: ${lastError?.message || 'Unknown error'}`;
  console.error(errorMessage);

  throw new Error(errorMessage);
}

// Helper function to clear hash cache for a project (useful when needed)
export function clearProjectHashCache(id?: string): void {
  if (id) {
    projectHashCache.delete(id);
  } else {
    projectHashCache.clear();
  }
}

export async function removeProject(
  id: string,
  { confirmed = false }: { confirmed?: boolean } = {}
): Promise<unknown> {
  const url = confirmed ? `/project/${id}/confirmed` : `/project/${id}`;
  const result = await request(url, {
    method: 'DELETE',
  });
  return result;
}

async function getProjectDataByJSON(url: string): Promise<unknown> {
  try {
    const data = await fetch(url).then((res) => res.json());
    return data;
  } catch {
    const fallback = url.replace('https://assets.seede.ai/', 'https://static.veeso.ai/');
    const data = await fetch(fallback).then((res) => res.json());
    return data;
  }
}

async function getProjectDataBySeede(url: string): Promise<unknown> {
  try {
    const data = await fetch(url).then((res) => res.text());
    return deserialize(data);
  } catch {
    const fallback = url.replace('https://assets.seede.ai/', 'https://static.veeso.ai/');
    const data = await fetch(fallback).then((res) => res.text());
    return deserialize(data);
  }
}

async function getProjectData(url: string): Promise<unknown> {
  const fileExtension = url.split('.').pop()?.split('?')[0];

  switch (fileExtension?.toLowerCase()) {
    case 'json':
      return await getProjectDataByJSON(url);
    case 'seede':
      return await getProjectDataBySeede(url);
    default:
      // Default to JSON if extension is unknown
      return await getProjectDataByJSON(url);
  }
}

interface Project {
  content: string;
  data?: unknown;
  [key: string]: unknown;
}

export async function getProject(id: string, accessPassword?: string): Promise<Project> {
  const query: Record<string, string> = {};
  if (accessPassword) {
    query.password = accessPassword;
  }
  const project = (await request(`/project/${id}`, { query })) as Project;
  const data = await getProjectData(project.content);
  project.data = data;
  return project;
}

export async function getTaskStatus(id: string): Promise<unknown> {
  return request(`/api/task/${id}/status`);
}

export async function claimDailyCredits(): Promise<unknown> {
  return request('/credits/daily', {
    method: 'POST',
  });
}

/**
 * 用户互动类型常量
 * User interaction type constants
 */
export const INTERACTION_TYPES = {
  DESIGN_INPUT: 'design_input', // 设计生成输入
  AI_RESPONSE: 'ai_response', // AI 响应/反馈
  CONVERSATION: 'conversation', // 对话交互
  FEEDBACK: 'feedback', // 用户反馈
  ITERATION: 'iteration', // 迭代修改
} as const;

interface UserInteractionParams {
  type: string;
  content: string;
  projectId?: string;
  meta?: Record<string, unknown>;
}

/**
 * 上报用户互动记录
 * Report user interaction for building user preference memory
 *
 * @param {Object} params - 互动参数
 * @param {string} params.type - 互动类型，使用 INTERACTION_TYPES 常量
 * @param {string} params.content - 互动内容
 * @param {string} [params.projectId] - 关联的项目 ID
 * @param {Object} [params.meta] - 额外的元数据
 * @returns {Promise<Object>} - API 响应
 */
export async function reportUserInteraction({
  type,
  content,
  projectId,
  meta = {},
}: UserInteractionParams): Promise<unknown> {
  if (!type || !content) {
    console.warn('reportUserInteraction: type and content are required');
    return null;
  }

  // 限制 content 最多 500 字符，避免过长
  const truncatedContent = content.length > 500 ? content.substring(0, 500) : content;

  try {
    const result = await request('/api/user-interaction', {
      method: 'POST',
      body: JSON.stringify({
        type,
        content: truncatedContent,
        projectId: projectId || null,
        meta,
      }),
    });
    return result;
  } catch (error) {
    // 静默失败，不影响主流程
    console.warn('Failed to report user interaction:', (error as Error).message);
    return null;
  }
}

export async function getDailyCreditsStatus(): Promise<unknown> {
  return request('/credits/daily/status');
}

export async function getTemplate(id: string): Promise<unknown> {
  const result = await request(`/template/${id}`, {
    method: 'GET',
  });
  return result;
}

interface EmptyProjectNode {
  id: string;
  type: string;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  index: string;
  fill?: string;
  parentId?: string;
  rotation?: number;
  text?: string;
  fontSize?: number;
  height?: string;
  highlights?: Array<{
    selection: [number, number];
    type: string;
    h: number;
    fill: string;
    id: string;
  }>;
  fontFamily?: string;
  styles?: null;
  fontWeight?: string;
  shadow?: string;
}

interface EmptyProject {
  type: string;
  props: {
    input: Record<string, unknown>;
    nodes: Record<string, EmptyProjectNode>;
  };
}

export const emptyProject: EmptyProject = {
  type: 'seedeGraphic',
  props: {
    input: {},
    nodes: {
      xd8es1lfb: {
        id: 'xd8es1lfb',
        type: 'frame',
        name: '',
        x: 0,
        y: 0,
        fill: '#ffffff',
        w: 1200,
        h: 628,
        index: 'a0',
      },
      '7k8b0a5n7': {
        rotation: 0,
        type: 'text',
        text: 'Hello Veeso',
        fontSize: 90,
        fill: 'linear-gradient(to bottom right, #ff6348, #ff9ff3, #3742fa)',
        height: 'Hug',
        x: 263,
        y: 659,
        w: 554,
        h: 122,
        parentId: 'xd8es1lfb',
        id: '7k8b0a5n7',
        name: 'Text',
        index: 'a1',
        highlights: [
          {
            selection: [6, 11],
            type: 'mark',
            h: 0.5,
            fill: 'linear-gradient(to bottom right, #ff7675, #74b9ff)',
            id: '0',
          },
        ],
        fontFamily: 'Poppins',
        styles: null,
        fontWeight: 'bold',
        shadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
      },
    },
  },
};

export function createEmptyProject({
  w = 1080,
  h = 1920,
}: { w?: number; h?: number } = {}): EmptyProject {
  const newProject = copy(emptyProject);
  const oldId = 'xd8es1lfb';
  const frame = newProject.props.nodes[oldId];
  delete newProject.props.nodes[oldId];
  const newId = createId();
  if (frame) {
    frame.id = newId;
    frame.w = w || frame.w;
    frame.h = h || frame.h;
    newProject.props.nodes[frame.id] = frame;
  }
  Object.values(newProject.props.nodes).map((node) => {
    if (node.parentId === oldId) node.parentId = newId;
  });
  return newProject;
}
